import {
  LABEEB_ENVIRONMENT_SETTINGS,
  type LabeebEnvironmentSettings,
} from "../../api/labeeb";
import {
  type FleetMetricEnvelope,
  metricPath,
  metricValue,
} from "../../api/mcp";
import { CouldNotLook, Denied, LoadFailed, Loading, SignedOut } from "../../components/states";
import { Badge, Card, Empty, Page, Stat, StatRow, Table, Td, Th } from "../../components/ui";
import type { AppConfig } from "../../config";
import { useResource } from "../../hooks/useResource";
import { ProductTag } from "../../products/PerProduct";
import { Settings } from "../governance/Settings";
import { AiAppsPanel } from "./AiApps";

/**
 * Settings — the design's Policy tab, one collapsible block per product.
 *
 * One page because it is one question: "what is this assistant configured to
 * do?" An operator should not have to know which product owns a knob to find
 * it. LABELLED by product because the answer to "did my change take effect?"
 * depends entirely on which one it was — an MCP message template needs an
 * explicit apply, an allowed AI app is live in ~60s, and a Labeeb domain gate
 * applies from each business's next turn.
 *
 * MCP's block holds its settings and, folded in beside them, the AI-app
 * allowlist — which is a setting in the same sense: which clients may connect.
 * It keeps its own route at `/policy/apps` as well, because a page is a URL
 * you can paste into an incident thread and a collapsed section is not.
 *
 * LABEEB'S HALF WAS REBUILT, not ported. Its old block held runtime flags and
 * a live-config editor; the new admin API has neither route
 * (`GET/PATCH /admin/flags` and `PATCH/DELETE /admin/config/{key}` are gone,
 * and `GET /admin/config` now returns deployment facts rather than a
 * key/value store). What replaces them is the one genuinely fleet-wide Labeeb
 * surface: the domains and plugins disabled for EVERY business.
 */
export function Flags({ config }: { config: AppConfig }) {
  // How much has been changed through this console today. Read-only: there is
  // no list on this page for a count to filter.
  const adminActions = useResource<FleetMetricEnvelope<"admin_actions_today">>(
    config,
    metricPath("admin_actions_today"),
  );
  const env = useResource<LabeebEnvironmentSettings>(config, LABEEB_ENVIRONMENT_SETTINGS);

  // What the environment is holding off, fleet-wide. A number worth a stat
  // because it is the one setting on this page that affects every merchant at
  // once — the rest are per-tier or per-app.
  const envOff =
    env.state.kind === "ready"
      ? (env.state.data?.settings?.disabled_domains?.length ?? 0) +
        (env.state.data?.settings?.disabled_plugins?.length ?? 0)
      : null;

  return (
    <Page
      title="Settings"
      subtitle="What the assistant is configured to do, in each product that configures it."
    >
      <div className="flex flex-col gap-6">
        <StatRow>
          <Stat
            value={metricValue(adminActions.state) ?? "…"}
            label="Admin actions today"
            hint="every write through this console, both products"
          />
          <Stat
            value={envOff ?? "…"}
            label="Disabled fleet-wide"
            tone={envOff ? "amber" : "neutral"}
            hint="Labeeb domains and plugins off for every business"
          />
        </StatRow>

        <Card
          title="MCP"
          aside={<ProductTag id="mcp" />}
          note="Refusal messages, the response shape, and which AI apps may connect"
          collapsible
        >
          <div className="flex flex-col gap-4">
            <Settings config={config} embedded />
            {/* COLLAPSED INSIDE A COLLAPSIBLE BLOCK, which is normally the
                nesting this console refuses — a block whose sections also
                fold reads as "the first one is all there is".
                It earns the exception: the allowlist is a long list of
                redirect URIs plus a table of refused apps, and left open it
                buries MCP's settings under a wall of addresses. So the
                settings stay flat and the allowlist is one line you open. */}
            <Card
              title="AI apps"
              note="Which AI apps merchants may connect to MCP"
              collapsible
            >
              <AiAppsPanel config={config} />
            </Card>
          </div>
        </Card>

        <Card
          title="Labeeb"
          aside={<ProductTag id="labeeb" />}
          note="Domains and plugins disabled for every business"
          collapsible
        >
          <EnvironmentSettings config={config} />
        </Card>
      </div>
    </Page>
  );
}

/**
 * The environment's gates: domains and plugins off for every business.
 *
 * READ-ONLY HERE, deliberately. `PATCH /admin/environment/settings` exists and
 * takes the same `expected_version` + `reason` + `Idempotency-Key` contract as
 * every other change, but one write here turns a capability off for the whole
 * fleet at once — a larger blast radius than anything else on this page. It
 * gets its own editor, with its own confirmation, rather than riding in as a
 * switch inside a collapsed block.
 *
 * Rendering the STORED list is correct here, unlike on a tenant page: this IS
 * the environment's own document, so there is nothing above it to override it.
 */
function EnvironmentSettings({ config }: { config: AppConfig }) {
  const env = useResource<LabeebEnvironmentSettings>(config, LABEEB_ENVIRONMENT_SETTINGS);
  const s = env.state;

  return (
    <>
      {s.kind === "loading" && <Loading what="the environment's settings" />}
      {s.kind === "signedout" && <SignedOut />}
      {s.kind === "denied" && <Denied message={s.error.message} />}
      {s.kind === "unavailable" && (
        <CouldNotLook
          what="the environment's settings"
          message={s.error.message}
          onRetry={env.reload}
        />
      )}
      {s.kind === "notfound" && (
        <LoadFailed what="the environment's settings" message="Route not found." />
      )}
      {(s.kind === "invalid" || s.kind === "error") && (
        <LoadFailed what="the environment's settings" message={s.error.message} />
      )}

      {s.kind === "ready" &&
        (() => {
          const domains = s.data?.settings?.disabled_domains ?? [];
          const plugins = s.data?.settings?.disabled_plugins ?? [];
          const version = s.data?.version;

          if (domains.length === 0 && plugins.length === 0) {
            return (
              <Empty>
                Nothing is disabled fleet-wide. Every domain and plugin is available to any
                business whose own settings allow it.
              </Empty>
            );
          }

          return (
            <div className="flex flex-col gap-4">
              <Table
                head={
                  <tr>
                    <Th>Off for every business</Th>
                    <Th>Kind</Th>
                  </tr>
                }
              >
                {domains.map((d) => (
                  <tr key={`d-${d}`}>
                    <Td>
                      <span className="ref">{d}</span>
                    </Td>
                    <Td>
                      <Badge tone="amber">domain</Badge>
                    </Td>
                  </tr>
                ))}
                {plugins.map((p) => (
                  <tr key={`p-${p}`}>
                    <Td>
                      <span className="ref">{p}</span>
                    </Td>
                    <Td>
                      <Badge tone="amber">plugin</Badge>
                    </Td>
                  </tr>
                ))}
              </Table>

              <p className="text-xs/5 text-slate-500 dark:text-slate-400">
                A business that enables one of these still cannot use it — the environment wins,
                and its merchant page says so per domain. Changes apply from each business&rsquo;s
                next turn.
                {version !== undefined && (
                  <>
                    {" "}
                    Document version <span className="ref">{version}</span>.
                  </>
                )}
              </p>
            </div>
          );
        })()}
    </>
  );
}
